import React from 'react';
import { Grid, Typography } from '@mui/material';
import GridContainer from 'components/GridContainer';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';
import Spacing from 'components/Spacing';
import RecordView from 'components/RecordView';
import FieldRow from 'components/FormElements/FieldRow';
import PageSection from 'components/PageSection';
import api from 'lib/trpcClient';
import { useNotification } from 'hooks/ui/useNotification';
import { useFormContext } from 'react-hook-form';
import { PartnerTransfersFormValues } from '../hooks/usePartnerTransfersForm';
import { useLayoutContext } from 'contexts/LayoutContext';
import {
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import { useLinkedCapitalActionsTransactions } from './hooks/useLinkedCapitalActionsTransactions';
import { useGeneralCapitalActionsTransactions } from './hooks/useGeneralCapitalActionsTransactions';
import useAdvanceSubscriptionsTransactions from './hooks/useAdvanceSubscriptionsTransactions';
import { useInitializeCapitalActionTransactions } from './hooks/useInitializeCapitalActionTransactions';
import CapitalActionTransactionAllocator from './CapitalActionTransactionAllocator';
import { useFeeTransactions } from './hooks/useFeeTransactions';
import { useInitializeFeeTransactions } from './hooks/useInitializeFeeTransactions';
import FeeTransactionAllocator from './FeeTransactionAllocator';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import PageLoader from 'components/PageLoader';
import ErrorBoundary from 'components/ErrorBoundary';

type Props = {
  onPrevious?: () => void;
  onApprove?: () => void;
};

const Review: React.FC<Props> = ({ onPrevious, onApprove }) => {
  const formMethods = useFormContext<PartnerTransfersFormValues>();
  const { selectedVehicleId } = useLayoutContext();
  const notification = useNotification();
  const utils = api.useUtils();

  const partnerTransferId = formMethods.getValues('partnerTransferId');

  const [partnerTransfer] = api.partnerTransfers.getById.useSuspenseQuery({
    partnerTransferId,
    vehicleId: selectedVehicleId ?? '',
  });

  const [vehicle] = api.vehicle.getById.useSuspenseQuery({
    vehicleId: selectedVehicleId ?? '',
  });

  const [linkedCapitalActionsTransactions] =
    api.partnerTransfers.getLinkedCapitalActionsTransactions.useSuspenseQuery({
      partnerTransferId,
      vehicleId: selectedVehicleId ?? '',
    });

  const [generalCapitalActionsTransactions] =
    api.partnerTransfers.getGeneralCapitalActionsTransactions.useSuspenseQuery({
      partnerTransferId,
      vehicleId: selectedVehicleId ?? '',
    });

  const [advanceSubscriptionsTransactions] =
    api.partnerTransfers.getAdvanceSubscriptionsTransactions.useSuspenseQuery({
      partnerTransferId,
      vehicleId: selectedVehicleId ?? '',
    });

  const [feeTransactions] =
    api.partnerTransfers.getFeeTransactions.useSuspenseQuery({
      partnerTransferId,
      vehicleId: selectedVehicleId ?? '',
    });

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const savedTransactions = formMethods.watch('capitalActionTransactions');
  const savedFeeTransactions = formMethods.watch('feeTransactions');

  const { groupedTransactions: linkedGroupedTransactions } =
    useLinkedCapitalActionsTransactions({
      linkedCapitalActionsTransactions,
      baseCurrency: vehicle.baseCurrency,
      savedTransactions,
    });

  const { groupedTransactions: generalGroupedTransactions } =
    useGeneralCapitalActionsTransactions({
      generalCapitalActionsTransactions,
      baseCurrency: vehicle.baseCurrency,
      savedTransactions,
    });

  const { groupedTransactions: advanceSubscriptionsGroupedTransactions } =
    useAdvanceSubscriptionsTransactions({
      advanceSubscriptionsTransactions,
      baseCurrency: vehicle.baseCurrency,
      savedTransactions,
    });

  const { groupedTransactions: groupedFeeTransactions } = useFeeTransactions({
    feeTransactions,
    baseCurrency: vehicle.baseCurrency,
    savedTransactions: savedFeeTransactions,
  });

  useInitializeCapitalActionTransactions({
    linkedGroupedTransactions,
    generalGroupedTransactions,
    advanceSubscriptionsGroupedTransactions,
    savedTransactions,
    formMethods,
  });

  useInitializeFeeTransactions({
    groupedTransactions: groupedFeeTransactions,
    savedTransactions: savedFeeTransactions,
    formMethods,
  });

  const approvePartnerTransferMutation =
    api.partnerTransfers.approve.useMutation({
      onSuccess: () => {
        notification.success('Partner transfer approved successfully');
        utils.commitments.list.invalidate();
        utils.lpFee.list.invalidate();
        if (onApprove) {
          onApprove();
        }
      },
      onError: (err) => {
        notification.error(
          err?.data?.cause ?? err?.message ?? 'Something went wrong.',
        );
      },
    });

  const handleApprove = async () => {
    if (!partnerTransferId || !selectedVehicleId) {
      notification.error('Missing required information to approve');
      return;
    }

    await approvePartnerTransferMutation.mutateAsync({
      partnerTransferId,
      vehicleId: selectedVehicleId,
      capitalActionTransactions: savedTransactions,
      feeTransactions: savedFeeTransactions,
    });
  };

  if (transactionTypesLoading) {
    return <PageLoader />;
  }

  if (!transactionTypeOptions.length) {
    return (
      <Typography>
        To set up account mappings, please first create some transaction types.
      </Typography>
    );
  }

  return (
    <GridContainer>
      <Grid size={{ xs: 12, lg: 8 }}>
        <Spacing size="md">
          <Typography variant="h4" gutterBottom>
            Review partner transfer
          </Typography>

          <PageSection title="Original commitment">
            <RecordView>
              <FieldRow title="LP">
                <Typography variant="body2">
                  {partnerTransfer.originalCommitment.lpName}
                </Typography>
              </FieldRow>
              <FieldRow title="Commitment amount">
                <Typography variant="body2">
                  {numberToCurrencyString(
                    partnerTransfer.originalCommitment.commitmentAmountLcy,
                    { currency: 'GBP' },
                  )}
                </Typography>
              </FieldRow>
              <FieldRow title="Amount to transfer">
                <Typography variant="body2">
                  {numberToDisplayString(
                    partnerTransfer.originalCommitment.transferPercentage,
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                  )}
                  %
                </Typography>
              </FieldRow>
            </RecordView>
          </PageSection>

          <PageSection title="New commitments">
            <RecordView>
              <FieldRow
                title={`${partnerTransfer.newCommitments.transferorName} (Transferor)`}
              >
                <Typography variant="body2">
                  {numberToCurrencyString(
                    partnerTransfer.newCommitments.transferorAmountLcy,
                    { currency: 'GBP' },
                  )}
                </Typography>
              </FieldRow>
              {partnerTransfer.newCommitments.transferees.map(
                (transferee, index) => (
                  <FieldRow
                    key={transferee.investorId}
                    title={`${transferee.investorName} (Transferee ${
                      index + 1
                    })`}
                  >
                    <Typography variant="body2">
                      {numberToCurrencyString(transferee.amountLcy, {
                        currency: 'GBP',
                      })}
                    </Typography>
                  </FieldRow>
                ),
              )}
            </RecordView>
          </PageSection>

          {linkedGroupedTransactions.length > 0 && (
            <PageSection title="Review linked capital actions transactions">
              <Spacing>
                {linkedGroupedTransactions.map((group) => (
                  <ErrorBoundary key={group.actionId}>
                    <CapitalActionTransactionAllocator
                      actionId={group.actionId}
                      actionName={group.actionName}
                      transactions={group.transactions}
                      transactionTypeOptions={transactionTypeOptions}
                      vehicle={vehicle}
                      savedTransactions={savedTransactions}
                    />
                  </ErrorBoundary>
                ))}
              </Spacing>
            </PageSection>
          )}

          {generalGroupedTransactions.length > 0 && (
            <PageSection title="Review general capital actions transactions">
              <Spacing>
                {generalGroupedTransactions.map((group) => (
                  <ErrorBoundary key={group.actionId}>
                    <CapitalActionTransactionAllocator
                      actionId={group.actionId}
                      actionName={group.actionName}
                      transactions={group.transactions}
                      transactionTypeOptions={transactionTypeOptions}
                      vehicle={vehicle}
                      savedTransactions={savedTransactions}
                    />
                  </ErrorBoundary>
                ))}
              </Spacing>
            </PageSection>
          )}

          {advanceSubscriptionsGroupedTransactions.length > 0 && (
            <PageSection title="Review advance subscriptions transactions">
              <Spacing>
                {advanceSubscriptionsGroupedTransactions.map((group) => (
                  <ErrorBoundary key={group.actionId}>
                    <CapitalActionTransactionAllocator
                      actionId={group.actionId}
                      actionName={group.actionName}
                      transactions={group.transactions}
                      transactionTypeOptions={transactionTypeOptions}
                      vehicle={vehicle}
                      savedTransactions={savedTransactions}
                    />
                  </ErrorBoundary>
                ))}
              </Spacing>
            </PageSection>
          )}

          {groupedFeeTransactions.length > 0 && (
            <PageSection title="Review fee transactions">
              <Spacing>
                {groupedFeeTransactions.map((group) => (
                  <ErrorBoundary key={group.feeId}>
                    <FeeTransactionAllocator
                      feeId={group.feeId}
                      feeName={group.feeName}
                      transactions={group.transactions}
                      transactionTypeOptions={transactionTypeOptions}
                      vehicle={vehicle}
                      savedTransactions={savedFeeTransactions}
                    />
                  </ErrorBoundary>
                ))}
              </Spacing>
            </PageSection>
          )}

          <ButtonGroup>
            {onPrevious && (
              <Button variant="outlined" onClick={onPrevious}>
                Previous
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleApprove}
              loading={approvePartnerTransferMutation.isPending}
              disabled={!partnerTransferId}
            >
              Approve transfer
            </Button>
          </ButtonGroup>
        </Spacing>
      </Grid>
    </GridContainer>
  );
};

export default Review;
